import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { authService } from '@/services/authService';
import { ApiError } from '@/lib/api';

// F132 — self-report layer of acquisition attribution. Kept in lockstep with
// the backend OneOf() in SignupRequestSchema (app/schemas/user_schema.py).
const ACQUISITION_SOURCES = ['instagram', 'creator', 'google', 'community', 'other'] as const;
type AcquisitionSource = (typeof ACQUISITION_SOURCES)[number];

const signupSchema = z.object({
  business_name: z.string().min(2, { message: 'auth.business_name_min' }).max(200),
  owner_name: z.string().min(2, { message: 'auth.owner_name_min' }).max(150),
  email: z.string().email({ message: 'auth.email_invalid' }),
  password: z.string().min(8, { message: 'auth.password_min' }).max(128),
  language: z.enum(['pt-BR', 'pt-PT']),
  // Both optional — empty string (no selection / no note) is the default and
  // is stripped from the payload in onSubmit rather than sent as ''.
  acquisition_source: z.enum([...ACQUISITION_SOURCES, '']),
  acquisition_note: z.string().max(200, { message: 'auth.acquisition.note_too_long' }),
});

type SignupFormValues = z.infer<typeof signupSchema>;

const RESEND_COOLDOWN_SECONDS = 30;

function resolveDefaultLanguage(detected: string | undefined): 'pt-BR' | 'pt-PT' {
  return detected === 'pt-PT' ? 'pt-PT' : 'pt-BR';
}

export function SignupPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  // F132 — captured once on mount, invisible to the user (no form field).
  // Held in state (rather than re-read on every render) so it survives any
  // later navigation-driven changes to the URL's query string.
  const [ref] = useState<string | undefined>(() => searchParams.get('ref') ?? undefined);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ email: string } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // F030 Layer 3 — public Turnstile site key. When empty (dev / no Cloudflare
  // config) the widget is skipped entirely and signup posts without a token.
  const turnstileSiteKey = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY as string | undefined;
  const turnstileEnabled = Boolean(turnstileSiteKey);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, []);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      business_name: '',
      owner_name: '',
      email: '',
      password: '',
      language: resolveDefaultLanguage(i18n.resolvedLanguage ?? i18n.language),
      acquisition_source: '',
      acquisition_note: '',
    },
  });

  // F132 — reveal the free-text note only when "other" is chosen.
  const acquisitionSource = form.watch('acquisition_source');

  async function onSubmit(values: SignupFormValues) {
    setSubmitError(null);
    try {
      await authService.signup({
        business_name: values.business_name,
        owner_name: values.owner_name,
        email: values.email,
        password: values.password,
        language: values.language,
        turnstile_token: turnstileToken ?? undefined,
        ref,
        acquisition_source: (values.acquisition_source || undefined) as
          | AcquisitionSource
          | undefined,
        acquisition_note: values.acquisition_note.trim() || undefined,
      });
      setSubmitted({ email: values.email });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError(t('auth.email_exists'));
      } else if (err instanceof ApiError && err.status === 422) {
        setSubmitError(t('auth.signup_rejected'));
      } else if (err instanceof ApiError && err.status === 429) {
        setSubmitError(t('auth.signup_rate_limited'));
      } else {
        setSubmitError(t('common.error_generic'));
      }
      // A used Turnstile token is single-use; reset so a retry re-challenges.
      setTurnstileToken(null);
    }
  }

  function startResendCooldown() {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  async function handleResend() {
    if (!submitted || resendCooldown > 0) return;
    try {
      await authService.resendVerification(submitted.email);
    } catch {
      // Endpoint returns 200 even when email is unknown (no enumeration); ignore.
    }
    startResendCooldown();
  }

  if (submitted) {
    return (
      <AuthShell title={t('auth.signup_success_title')}>
        <div className="space-y-6">
          <div className="rounded-lg border border-border p-6 text-center space-y-3">
            <Mail className="h-8 w-8 mx-auto text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              {t('auth.signup_success_body', { email: submitted.email })}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            className="w-full h-11"
            onClick={handleResend}
            disabled={resendCooldown > 0}
          >
            {resendCooldown > 0
              ? `${t('auth.resend_email')} (${resendCooldown}s)`
              : t('auth.resend_email')}
          </Button>
          <p className="text-center text-sm">
            <Link
              to="/login"
              className="text-foreground font-medium underline-offset-4 hover:underline"
            >
              {t('auth.log_in')}
            </Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('auth.signup_title')}
      subtitle={t('auth.signup_subtitle')}
      footer={
        <>
          {t('auth.have_account')}{' '}
          <Link
            to="/login"
            className="text-foreground font-medium underline-offset-4 hover:underline"
          >
            {t('auth.log_in')}
          </Link>
        </>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="business_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.business_name')}</FormLabel>
                <FormControl>
                  <Input className="h-11" autoFocus autoComplete="organization" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="owner_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.owner_name')}</FormLabel>
                <FormControl>
                  <Input className="h-11" autoComplete="name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.email')}</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="email" className="h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" className="h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="language"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.language')}</FormLabel>
                <FormControl>
                  <select
                    name={field.name}
                    ref={field.ref}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={(event) => {
                      // The select value drives the bot language (tenant.language)
                      // via the register payload AND switches the UI locale
                      // immediately so the form + success screen render in it.
                      field.onChange(event.target.value);
                      void i18n.changeLanguage(event.target.value);
                    }}
                    className="flex h-11 w-full rounded-lg border border-border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="pt-BR">Português (Brasil)</option>
                    <option value="pt-PT">Português (Portugal)</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* F132 — self-report acquisition layer. Optional, empty default;
              invisible to nothing else in the payload construction (unlike
              `ref`, this one IS a real form field). */}
          <FormField
            control={form.control}
            name="acquisition_source"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.acquisition.label')}</FormLabel>
                <FormControl>
                  <select
                    name={field.name}
                    ref={field.ref}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={(event) => field.onChange(event.target.value)}
                    className="flex h-11 w-full rounded-lg border border-border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">{t('auth.acquisition.placeholder')}</option>
                    <option value="instagram">{t('auth.acquisition.option_instagram')}</option>
                    <option value="creator">{t('auth.acquisition.option_creator')}</option>
                    <option value="google">{t('auth.acquisition.option_google')}</option>
                    <option value="community">{t('auth.acquisition.option_community')}</option>
                    <option value="other">{t('auth.acquisition.option_other')}</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {acquisitionSource === 'other' && (
            <FormField
              control={form.control}
              name="acquisition_note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.acquisition.note_label')}</FormLabel>
                  <FormControl>
                    <Input
                      className="h-11"
                      maxLength={200}
                      placeholder={t('auth.acquisition.note_placeholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {turnstileEnabled && (
            <div data-testid="turnstile-widget">
              <Turnstile
                siteKey={turnstileSiteKey as string}
                onSuccess={setTurnstileToken}
                onError={() => setTurnstileToken(null)}
                onExpire={() => setTurnstileToken(null)}
                options={{ appearance: 'interaction-only' }}
              />
            </div>
          )}

          {submitError && (
            <div role="alert" className="text-sm text-destructive">
              {submitError}
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-11"
            disabled={form.formState.isSubmitting || (turnstileEnabled && !turnstileToken)}
          >
            {form.formState.isSubmitting ? t('common.loading') : t('auth.signup_button')}
          </Button>
        </form>
      </Form>
    </AuthShell>
  );
}
